import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, Grid } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle as CheckIcon,
  Flag as FlagIcon,
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
  PictureAsPdf as PdfIcon,
  AutoGraph as ObjectiveIcon,
  Shield as RiskIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';

/* ── Animations ──────────────────────────────────────────────── */
const floatUp = keyframes`
  0%, 100% { transform: translateY(0px);   }
  50%       { transform: translateY(-8px);  }
`;
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0);    }
`;
const pulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(1);    }
  50%       { opacity: 0.65; transform: scale(1.08); }
`;
const barGrow = keyframes`
  from { width: 0%; }
  to   { width: 72%; }
`;
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center;  }
`;

/* ── Static step keys (labels come from i18n `steps.*`) ────── */
const STEP_KEYS = [
  { key: 'steps.vision',               done: true  },
  { key: 'steps.situation_analysis',   done: true  },
  { key: 'steps.strategic_objectives', done: true  },
  { key: 'steps.action_components',    done: true  },
  { key: 'steps.resources',            done: false },
  { key: 'steps.timeline',             done: false },
  { key: 'steps.risks',                done: false },
  { key: 'steps.governance',           done: false },
  { key: 'steps.performance',          done: false },
  { key: 'steps.adaptation',           done: false },
];

const FEATURE_ICONS = [
  <TrendingIcon  sx={{ fontSize: 16 }} />,
  <PeopleIcon    sx={{ fontSize: 16 }} />,
  <ObjectiveIcon sx={{ fontSize: 16 }} />,
  <RiskIcon      sx={{ fontSize: 16 }} />,
  <TimelineIcon  sx={{ fontSize: 16 }} />,
  <PdfIcon       sx={{ fontSize: 16 }} />,
];

/* ════════════════════════════════════════════════════════════════ */
export default function AuthMarketingPanel() {
  const { t } = useTranslation();
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [visible, setVisible]     = useState(true);

  const PHRASES = [
    t('auth.marketing.phrase1'),
    t('auth.marketing.phrase2'),
    t('auth.marketing.phrase3'),
    t('auth.marketing.phrase4'),
  ];

  const STATS = [
    { value: '10',   label: t('auth.marketing.statsSteps'),   color: '#6366f1' },
    { value: '100%', label: t('auth.marketing.statsCloud'),   color: 'var(--status-green)' },
    { value: '3',    label: t('auth.marketing.statsFormats'), color: 'var(--status-sky)' },
  ];

  const FEATURES = FEATURE_ICONS.map((icon, i) => ({
    icon,
    label: t(`auth.marketing.feature${i + 1}Label`),
    desc:  t(`auth.marketing.feature${i + 1}Desc`),
  }));

  useEffect(() => {
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setPhraseIdx(i => (i + 1) % PHRASES.length);
        setVisible(true);
      }, 400);
    }, 3800);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completedCount = STEP_KEYS.filter(s => s.done).length;

  return (
    <Box sx={{
      flex: 1,
      minHeight: '100vh',
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'stretch',
      px: { md: 5, lg: 7 },
      py: { md: 5, lg: 6 },
      position: 'relative',
      overflow: 'hidden',
      /* vertical divider */
      '&::before': {
        content: '""', position: 'absolute',
        left: 0, top: 0, bottom: 0, width: 1,
        background: 'linear-gradient(180deg, transparent 0%, rgba(99,102,241,0.25) 30%, rgba(99,102,241,0.25) 70%, transparent 100%)'
      }
    }}>

      {/* ── Background atmosphere ── */}
      <Box sx={{
        position: 'absolute', top: '-5%', right: '-5%',
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.09) 0%, transparent 65%)',
        animation: `${pulse} 7s ease-in-out infinite`,
        pointerEvents: 'none'
      }} />
      <Box sx={{
        position: 'absolute', bottom: '5%', left: '-8%',
        width: 350, height: 350, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)',
        animation: `${pulse} 9s ease-in-out infinite 1.5s`,
        pointerEvents: 'none'
      }} />
      <Box sx={{
        position: 'absolute', top: '40%', right: '15%',
        width: 180, height: 180, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(56,189,248,0.05) 0%, transparent 65%)',
        animation: `${pulse} 5s ease-in-out infinite 0.8s`,
        pointerEvents: 'none'
      }} />

      {/* ── TOP: Brand + headline ── */}
      <Box sx={{ animation: `${fadeIn} 0.6s ease both` }}>
        {/* Logo */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 1.5,
            bgcolor: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <FlagIcon sx={{ color: '#6366f1', fontSize: 19 }} />
          </Box>
          <Typography sx={{
            fontSize: '0.8rem', fontWeight: 700,
            color: 'var(--fg-faint)',
            letterSpacing: 2, textTransform: 'uppercase'
          }}>
            {t('auth.marketing.appName')}
          </Typography>
        </Stack>

        {/* Headline */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{
            fontSize: { md: '2.2rem', lg: '2.8rem', xl: '3.2rem' },
            fontWeight: 900, lineHeight: 1.1, color: 'var(--fg-strong)', mb: 0.5
          }}>
            {t('auth.marketing.headline1')}
          </Typography>
          <Typography sx={{
            fontSize: { md: '2.2rem', lg: '2.8rem', xl: '3.2rem' },
            fontWeight: 900, lineHeight: 1.1,
            background: 'linear-gradient(90deg, #6366f1, #3b82f6, #38bdf8, #6366f1)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: `${shimmer} 4s linear infinite`
          }}>
            {t('auth.marketing.headline2')}
          </Typography>
        </Box>

        {/* Cycling phrase */}
        <Box sx={{ height: 40 }}>
          <Typography sx={{
            fontSize: '0.95rem', color: 'var(--fg-faint)', lineHeight: 1.5,
            transition: 'opacity 0.4s ease, transform 0.4s ease',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(5px)'
          }}>
            {PHRASES[phraseIdx]}
          </Typography>
        </Box>
      </Box>

      {/* ── MIDDLE: Stats row ── */}
      <Box sx={{ animation: `${fadeIn} 0.7s ease 0.15s both` }}>
        <Stack direction="row" spacing={2}>
          {STATS.map((stat) => (
            <Box key={stat.label} sx={{
              flex: 1, bgcolor: 'var(--auth-panel)',
              border: '1px solid var(--line-1)',
              borderRadius: 2, p: 2, textAlign: 'center'
            }}>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: stat.color, lineHeight: 1, mb: 0.5 }}>
                {stat.value}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'var(--fg-faint)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* ── MAIN: Floating roadmap card ── */}
      <Box sx={{
        bgcolor: 'var(--auth-panel)',
        border: '1px solid var(--line-2)',
        borderRadius: 2, p: 3,
        animation: `${floatUp} 6s ease-in-out infinite, ${fadeIn} 0.8s ease 0.25s both`,
        position: 'relative',
        overflow: 'hidden',
        /* top accent */
        '&::before': {
          content: '""', position: 'absolute',
          top: 0, left: 0, right: 0, height: 2,
          background: 'linear-gradient(90deg, #6366f1, #3b82f6, #38bdf8)'
        }
      }}>
        {/* Card header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('auth.marketing.cardTitle')}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-faint)' }}>
              {completedCount}/{STEP_KEYS.length} {t('auth.marketing.cardStepsOf')}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#6366f1' }}>72%</Typography>
          </Stack>
        </Stack>

        {/* Progress bar */}
        <Box sx={{ height: 3, bgcolor: 'var(--line-1)', borderRadius: 10, mb: 2.5, overflow: 'hidden' }}>
          <Box sx={{
            height: '100%', borderRadius: 10,
            background: 'linear-gradient(90deg, #6366f1, #3b82f6, #38bdf8)',
            animation: `${barGrow} 2s cubic-bezier(0.4,0,0.2,1) 0.5s both`
          }} />
        </Box>

        {/* All 10 steps — 2 columns */}
        <Grid container spacing={0.5}>
          {STEP_KEYS.map((step, i) => {
            const fullLabel = t(step.key);
            const label = fullLabel.replace(/^\d+\.\s*/, '');
            return (
            <Grid item xs={6} key={step.key}>
              <Stack direction="row" alignItems="center" spacing={1}
                sx={{ animation: `${fadeIn} 0.35s ease ${0.6 + i * 0.07}s both` }}>
                <Box sx={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: step.done ? 'rgba(34,197,94,0.1)' : 'var(--line-1)',
                  border: '1px solid ' + (step.done ? 'rgba(34,197,94,0.28)' : 'var(--line-1)')
                }}>
                  {step.done
                    ? <CheckIcon sx={{ fontSize: 11, color: 'var(--status-green)' }} />
                    : <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, color: 'var(--line-soft)' }}>{i + 1}</Typography>}
                </Box>
                <Typography sx={{
                  fontSize: '0.75rem', lineHeight: 1.3,
                  color: step.done ? 'var(--fg-muted)' : 'var(--line-soft)',
                  fontWeight: step.done ? 500 : 400,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  {label}
                </Typography>
              </Stack>
            </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* ── BOTTOM: Feature grid ── */}
      <Box sx={{ animation: `${fadeIn} 0.8s ease 0.4s both` }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--line-soft)', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
          {t('auth.marketing.featureSection')}
        </Typography>
        <Grid container spacing={1.25}>
          {FEATURES.map((feat, i) => (
            <Grid item xs={6} key={i}>
              <Stack direction="row" alignItems="flex-start" spacing={1.25}
                sx={{
                  bgcolor: 'var(--auth-panel)',
                  border: '1px solid var(--line-1)',
                  borderRadius: 1.5, p: 1.5,
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: 'rgba(99,102,241,0.2)' }
                }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: 1, flexShrink: 0,
                  bgcolor: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#6366f1'
                }}>
                  {feat.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--fg-muted)', lineHeight: 1.2, mb: 0.25 }}>
                    {feat.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'var(--fg-faint)', lineHeight: 1.3 }}>
                    {feat.desc}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>

    </Box>
  );
}
